import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import './Call.css';
import { ShareCard } from '../components/ShareCard/ShareCard.tsx';
import type { JeffCall } from '../../shared/types.ts';
import { normaliseCallId } from '../../shared/ids.ts';
import { fetchCall } from '../lib/calls.ts';
import { formatCallTimestamp } from '../lib/formatting.ts';
import { JEFF } from '../config/jeff.ts';
import { useDocumentTitle } from '../lib/hooks.ts';

type State =
  | { status: 'loading' }
  | { status: 'found'; call: JeffCall }
  | { status: 'missing' };

/**
 * The permalink.
 *
 * This is what a Discord link resolves to, so it has to stand alone: the card,
 * the verdict as text, and enough metadata to be checkable. The Worker has
 * already injected per-call OpenGraph tags into the HTML for the unfurl; this
 * component renders what a human sees after they click.
 */
export function Call() {
  const { id } = useParams<{ id: string }>();
  // Remounting per ID is what lets the view below start from a clean `loading`
  // state without resetting anything in an effect. Navigating between two
  // permalinks therefore shows a spinner, not the previous call's data.
  return <CallView key={id ?? ''} raw={id} />;
}

function CallView({ raw }: { raw: string | undefined }) {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'loading' });

  const id = raw ? normaliseCallId(raw) : null;

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();

    void fetchCall(id, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      setState(result ? { status: 'found', ...result } : { status: 'missing' });
    });

    return () => controller.abort();
  }, [id]);

  // A malformed ID never reaches the network; it is simply not a call.
  const view: State = id ? state : { status: 'missing' };

  useDocumentTitle(
    view.status === 'found'
      ? `${view.call.asset ? `${view.call.asset} · ` : ''}${view.call.direction} — ${JEFF.appName}`
      : `Call ${id ?? ''} — ${JEFF.appName}`,
  );

  if (view.status === 'loading') {
    return (
      <main className="callpage" id="main">
        <p className="callpage__status">Retrieving call{id ? ` ${id}` : ''}…</p>
      </main>
    );
  }

  if (view.status === 'missing') {
    return (
      <main className="callpage" id="main">
        <div className="callpage__empty panel">
          <h1 className="callpage__empty-title">Call not found</h1>
          <p className="callpage__empty-body">
            {id ? (
              <>
                <code>{id}</code> is not in the record.
              </>
            ) : (
              <>That is not a valid call ID. They look like {'JEFF-7F3A2B'}.</>
            )}
          </p>
          <Link className="btn btn--primary" to="/">
            Release Jeff
          </Link>
        </div>
      </main>
    );
  }

  const { call } = view;

  return (
    <main className="callpage" id="main">
      <p className="callpage__eyebrow">Official Jeff call</p>

      <ShareCard call={call} />

      {/* The card is an image to a screen reader's user; restate it as text. */}
      <p className="callpage__verdict">
        Jeff went <b className={`callpage__dir callpage__dir--${call.direction.toLowerCase()}`}>{call.direction}</b>
        {call.asset ? ` on ${call.asset}.` : '.'}
      </p>

      <dl className="callpage__meta">
        <div>
          <dt>Call ID</dt>
          <dd>{call.id}</dd>
        </div>
        <div>
          <dt>Timestamp</dt>
          <dd>{formatCallTimestamp(call.createdAt)}</dd>
        </div>
        <div>
          <dt>Analyst</dt>
          <dd>Jeff · {call.jeffVersion}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{call.status}</dd>
        </div>
        {call.entropy && (
          <div className="callpage__meta-wide">
            <dt>Draw entropy</dt>
            <dd>
              <code>0x{call.entropy}</code>
            </dd>
          </div>
        )}
      </dl>

      <div className="callpage__actions">
        <button type="button" className="btn btn--primary" onClick={() => void navigate('/')}>
          Make a call
        </button>
      </div>

    </main>
  );
}
